import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "@/lib/utils.ts";

const smStyles = "h-9 rounded-full px-3";

export const buttonVariantsConfig = {
  variant: {
    // TODO: when doing rebrand work, rework terms like "secondary" (our secondary buttons don't
    // use our secondary color, they use an accent color) and popover to be more consistent.

    /** Primary color, "holo" button style, filled on hover. */
    default:
      "border-primary bg-transparent text-primary hover:bg-primary hover:text-primary-foreground",

    /** Like default, but using our secondary color */
    outline:
      "border-input bg-transparent hover:bg-accent hover:text-accent-foreground hover:border-accent",

    /** Outline variant, to be used on top of popover bg */
    outlinePopover:
      "border-input bg-transparent hover:bg-secondary hover:text-secondary-foreground hover:border-secondary",

    /** Ghost (no border or bg when unhovered) */
    ghost: "border-transparent bg-transparent hover:bg-accent hover:text-accent-foreground",

    /** Ghost variant, to be used on top of popover bg */
    ghostPopover:
      "border-transparent bg-transparent hover:bg-secondary hover:text-secondary-foreground",

    /** "Fill" button using secondary color. */
    secondary: "bg-accent text-accent-foreground hover:bg-accent/70",

    /** Outline variant, to be used on top of secondary color */
    secondaryOutline:
      "border-accent bg-transparent text-accent hover:bg-accent hover:text-accent-foreground hover:border-accent",

    /** Secondary variant, to be used on top of popover bg. */
    secondaryPopover: "bg-secondary text-secondary-foreground hover:bg-secondary/70",

    /** "Fill" button, using primary color. Use sparingly - we generally prefer "default". */
    primary: "bg-primary text-primary-foreground hover:bg-primary/90",

    /** "Fill" button of the popover color.  */
    popover: "bg-popover text-popover-foreground hover:bg-accent",

    /** For use within a form as a select element */
    formSelect:
      "bg-popover text-popover-foreground hover:bg-accent border-input font-normal justify-start",

    /** Looks like text */
    link: "text-primary underline-offset-4 hover:underline",

    /** Unstyled, used as a base for buttons with custom styles. Generally an anti-patten. */
    blank: "",

    /** Angry looking "fill" */
    destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",

    /** Gradient text with gradient fill on hover */
    gradient:
      "border-transparent bg-transparent text-transparent bg-gradient-to-br from-brand-500 to-[#c084fc] bg-clip-text transition-all duration-300 hover:bg-gradient-to-br hover:from-[#5f79f5] hover:to-[#c084fc] hover:text-white hover:bg-clip-border",

    /** Gradient outline border with gradient text */
    gradientOutline:
      "border-primary text-transparent bg-gradient-to-br from-brand-500 to-[#c084fc] bg-clip-text transition-all duration-300 hover:bg-gradient-to-br hover:from-[#5f79f5] hover:to-[#c084fc] hover:text-white hover:border-transparent hover:bg-clip-border",
  },
  size: {
    default: "h-10 rounded-full px-4 py-2",
    "2xs": "h-6 rounded-full px-2 py-1 text-xs",
    xs: "h-7 rounded-full px-3 py-1",
    sm: smStyles,
    lg: "h-11 rounded-full px-8",
    icon: "h-10 rounded-full w-10",
    action: "p-1 rounded-full",
    fit: "rounded-full",

    /** For use within a form as a select element */
    formSelect: "py-3.5 px-4.5 rounded-xl",

    // "Responsive" sizes that change depending on the container size.
    // Design generally does not change button size between different breakpoints, so we should
    // generally jsut use the normal "fixed" size options. But, we have these for the edge cases where
    // they do need to change, so we don't need to calculate breakpoints in JS.
    // TODO: this is a hack. We could probably do better - e.g. bake these into default variants (or flipping, making those "f" for fixed prefixed)
    // a hook, different props for different media query sizes, etc, a way to map and prefix with the container query variant, etc.
    rSmDefault: cn(smStyles, "@md:h-10 @md:px-4 @md:py-2"),
    rSmLg: cn(smStyles, "@md:h-11 @md:px-8"),
  },
} as const;

export const buttonDefaultVariants = {
  variant: "default",
  size: "default",
} as const satisfies {
  [K in keyof typeof buttonVariantsConfig]: keyof (typeof buttonVariantsConfig)[K];
};

const buttonVariants = cva(
  // apply border to all, defaulting to transparent (will show the bg-* color underneath)
  "border border-transparent inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium group/button cursor-pointer ring-offset-background transition-colors focus-visible:outline-hidden kbd-nav:focus-visible:ring-2 kbd-nav:focus-visible:ring-ring kbd-nav:focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none",
  {
    variants: buttonVariantsConfig,
    defaultVariants: buttonDefaultVariants,
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  /**
   * Required tracking name for Statsig analytics.
   * Use kebab-case format: {feature}-{action} (e.g., "thread-send-message", "nav-logout")
   */
  "data-statsig-name": string;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
